<template>
  <basic-card>
    <template slot="header">{{ operationDefinition.name }} ({{ operationDefinition.id }})</template>
    <form-wrapper>
      <form-text-input :caption="`${$t('name')}`" v-model="internalDefinition.name"/>
      <form-text-input :caption="`${$t('code')}`" v-model="internalDefinition.code"/>
      <form-text-input :caption="`${$t('description')}`" v-model="internalDefinition.description"/>
      <form-select-input caption="Defaults script" v-model="internalDefinition.defaultsScript"
                         :options="importDefinitionsOptions"/>
      <form-checkbox-input :caption="`${$t('enableSoftDelete')}`" v-model="internalDefinition.softDeleteEnabled"/>
      <form-checkbox-input :caption="`${$t('enableUpdates')}`" v-model="internalDefinition.updateEnabled"/>
      <form-checkbox-input :caption="`${$t('enableDrafts')}`"  v-model="internalDefinition.draftEnabled"/>
    </form-wrapper>

    <collapsible-content>
      <template slot="header">
          {{ $t('parameters') }}
      </template>
      <data-table
          :data="internalParameters"
          :configuration="parameterTableConfig"
          @add="parameterAdd"
          @remove="parameterRemove"
          @edit="parameterEdit"
          @moveUp="parameterMoveUp"
          @moveDown="parameterMoveDown"
          component-id="operation-definition-parameters-list"/>

    </collapsible-content>
    <collapsible-content v-if="internalDefinition.id">
      <template slot="header">Indices & Constraints</template>
      <div style="display: flex; flex-flow: row;">
        <simple-button @press="addUniqueIndex" :disabled="operationIndices.length > 0">Add unique index
        </simple-button>
      </div>
      <div v-for="operationIndex in operationIndices">
        <div style="background-color: #586674; padding: 10px; margin: 10px 0px 10px 0px;">
          <h1>Unique constraint [{{ operationIndex.id }}]</h1>

          <h1>Fields</h1>
          <div style="display: flex; flex-flow: row; flex-wrap: wrap;"
               class="columns-item"
               :style="{'background-color': operationIndex.fields.includes(`source`) ? '#C84A4B' : undefined}"
               @click="toggleIndexField(operationIndex, `source`)">
            <icon :icon="['fal', operationIndex.fields.includes(`source`) ? 'check-square' : 'square' ]"/>&nbsp;
            Source
          </div>
          <div class="columns-item"
               :style=" {'background-color': operationIndex.fields.includes(`content.values.${paramField.name}`) ? '#C84A4B' : undefined}"
               v-for="paramField in internalParameters"
               @click="toggleIndexField(operationIndex, `content.values.${paramField.name}`)">
            <icon
                :icon="['fal', operationIndex.fields.includes(`content.values.${paramField.name}`) ? 'check-square' : 'square' ]"/>&nbsp;
            {{ paramField.displayName }}
          </div>
        </div>
        <h1>Actions</h1>
        <div style="display: flex; flex-flow: row; column-gap: 10px;">
          <simple-button @press="saveIndex(operationIndex)">
            <icon :icon="['fal', 'save']"/>
            Save unique constraint
          </simple-button>
          <simple-button @press="deleteIndex(operationIndex)">
            <icon :icon="['fal', 'trash']"/>
            Delete unique constraint
          </simple-button>
        </div>
      </div>


    </collapsible-content>


    <collapsible-content start-collapsed v-if="internalDefinition.id">
      <template slot="header">
        Relations
      </template>
    </collapsible-content>

    <v-modal name="paramModal" classes="transparent-modal" style="background-color: rgba(10,10,10,0.66)"
             :width="800" :height="600">
      <basic-card v-if="focusedParam != null">
        <template slot="header">
            {{ $t('parameter') }}
        </template>
        <form-wrapper>
          <form-text-input :caption="`${$t('systemName')}`" v-model="focusedParam.name"/>
          <form-text-input :caption="`${$t('displayName')}`" v-model="focusedParam.displayName"/>
          <form-multi-select-input caption="Recalculation script" v-model="focusedParam.recalculationScripts"
                        :options="importDefinitionsOptions"/>
          <form-select-input :caption="`${$t('propertyType')}`" v-model="focusedParam.details.type"
                             :options="typeOptions"/>
          <form-checkbox-input :caption="`${$t('required')}`" v-model="focusedParam.details.required"/>
          <form-checkbox-input :caption="`${$t('allowInvalidInput')}`" v-model="focusedParam.details.allowInvalid"/>
        </form-wrapper>
        <template v-if="focusedParam.details.type == 'STRING'">
          <form-wrapper>
            <form-number-input :caption="`${$t('maxLength')}`" v-model="focusedParam.details.maxLength"/>
            <form-text-input :caption="`${$t('regularExpression')}`"
                             v-model="focusedParam.details.regularExpression"/>
          </form-wrapper>
        </template>
        <template v-if="focusedParam.details.type == 'DECIMAL'">
          <form-wrapper>
            <form-number-input :caption="`${$t('precision')}`" v-model="focusedParam.details.precision"/>
            <form-number-input :caption="`${$t('minValue')}`" v-model="focusedParam.details.minValue"/>
            <form-number-input :caption="`${$t('maxValue')}`" v-model="focusedParam.details.maxValue"/>
          </form-wrapper>
        </template>
        <template v-if="focusedParam.details.type == 'INTEGER'">
          <form-wrapper>
            <form-number-input :caption="`${$t('minValue')}`" v-model="focusedParam.details.minValue"/>
            <form-number-input :caption="`${$t('maxValue')}`" v-model="focusedParam.details.maxValue"/>
          </form-wrapper>
        </template>
        <template v-if="focusedParam.details.type == 'ENTITY_REFERENCE'">
          <form-wrapper>
            <form-select-input :caption="`${$t('entityType')}`" v-model="focusedParam.details.entityType"
                               :options="entityTypes"/>
          </form-wrapper>
          <collapsible-content v-if="focusedParam.details.entityType == 'account'">
            <template slot="header">
              Query
            </template>
            <filter-form
                :filter-definition="queryDefinition(focusedParam.details.entityType)"
                v-model="focusedParam.details.entityFilter"/>
          </collapsible-content>
          <collapsible-content v-else-if="focusedParam.details.entityType == 'operation'">
            <template slot="header">
              Definition
            </template>
            <form-wrapper>
              <form-select-input caption="Operation definition" :options="operationDefinitions"
                                 v-model="focusedParamOperationDefinition"/>
            </form-wrapper>
            <filter-form v-if="focusedParamOperationDefinition"
                         :filter-definition="queryDefinition(focusedParam.details.entityType)"
                         v-model="focusedParam.details.entityFilter"/>

          </collapsible-content>
          <collapsible-content>
            <template slot="header">
              Query bindings
            </template>
            <div style="display: flex; flex-flow: column;">
              <div v-for="queryBinding in focusedParam.details.entityFilterBindings"
                   style="background-color: #414b55; margin: 5px; padding: 5px;">
                <form-wrapper>
                  <form-text-input v-model="queryBinding.searchQueryKey" caption="Query key"/>
                  <form-text-input v-model="queryBinding.operatorExpression"
                                   caption="Operator expression"/>
                  <form-text-input v-model="queryBinding.valueExpression"
                                   caption="Value expression"/>
                </form-wrapper>
              </div>
              <div>
                <simple-button
                    @press="focusedParam.details.entityFilterBindings.push({searchQueryKey: '', valueExpression: '', operatorExpression: ''})">
                  +
                </simple-button>
              </div>
            </div>
          </collapsible-content>
        </template>
        <template v-if="focusedParam.details.type == 'ENUM'">
          <div style="display: flex; flex-flow: column;">
            <h1>{{ $t('enumValues')}}</h1>
            <div v-for="enumEntry in focusedParam.details.enumValues"
                 style="background-color: #414b55; margin: 5px; padding: 5px;">
              <form-wrapper>
                <form-text-input v-model="enumEntry.name" :caption="`${$t('name')}`"/>
                <form-text-input v-model="enumEntry.value" caption="Value"/>
              </form-wrapper>
            </div>
            <div>
              <simple-button
                  @press="focusedParam.details.enumValues.push({name: 'N/A', value: 'N/A'})">+
              </simple-button>
            </div>
          </div>
        </template>
        <simple-button @press="saveParam">{{$t('saveParameter')}}</simple-button>
      </basic-card>
    </v-modal>

    <simple-button @press="save">{{$t("save")}}</simple-button>
  </basic-card>
</template>

<script>
import BasicCard from "../components/layout/BasicCard";
import FormWrapper from "../components/form/FormWrapper";
import FormTextInput from "../components/form/FormTextInput";
import DataTable from "../components/table/DataTable";
import SimpleButton from "../components/button/SimpleButton";
import SimpleHorizontalLayout from "../components/layout/SimpleHorizontalLayout";
import FormSelectInput from "../components/form/FormSelectInput";
import FormCheckboxInput from "../components/form/FormCheckboxInput";
import CollapsibleContent from "../components/CollapsibleContent";
import Toolbox from "../components/toolbox/Toolbox";
import Tabs from "../components/RouterTabs";
import RouterTabs from "../components/RouterTabs";
import FormNumberInput from "../components/form/FormNumberInput";
import FilterForm from "../components/filters/FilterForm";
import QueryUtils from "../utils/QueryUtils";
import {mapGetters} from "vuex";
import FormMultiSelectInput from "@/components/form/FormMultiSelectInput.vue";

export default {
  name: "OperationDefinitionFormCard",
  components: {
    FormMultiSelectInput,
    FilterForm,
    FormNumberInput,
    RouterTabs,
    Tabs,
    Toolbox,
    CollapsibleContent,
    FormCheckboxInput,
    FormSelectInput,
    SimpleHorizontalLayout, SimpleButton, DataTable, FormTextInput, FormWrapper, BasicCard
  },
  props: {
    operationDefinition: Object
  },
  data: function () {
    return {
      internalDefinition: null,
      internalParameters: null,
      operationIndices: [],
      typeOptions: [
        {id: "DATE", name: "Date"},
        {id: "DATE_TIME", name: "DateTime"},
        {id: "STRING", name: "String"},
        {id: "DECIMAL", name: "Decimal"},
        {id: "INTEGER", name: "Integer"},
        {id: "ENTITY_REFERENCE", name: "Reference"},
        {id: "ENUM", name: "Enumeration"},
        //{id: "ARRAY", name: "Array"},
        {id: "BOOLEAN", name: "Boolean"},
        {id: "GEO_POINT", name: "Geo Point"}
      ],
      entityTypes: [
        {id: "currency", name: "Currency"},
        {id: "party", name: "Party"},
        {id: "partyRole", name: "Party Role"},
        {id: "account", name: "Account"},
        {id: "operation", name: "Operation"}
      ],
      focusedParam: null,
      originalParamName: "",
      focusedParamOperationDefinition: null,
      parameterTableConfig: {
        enableAdd: true,
        allowEdit: true,
        selectionMode: 'single',
        columns: [
          {
            caption: this.$t('name'),
            datum: d => d.name
          },
          {
            caption:this.$t('displayName'),
            datum: d => d.displayName
          },
          {
            caption: this.$t('type'),
            datum: d => d.details.type
          },
          {
            caption:this.$t('required'),
            datum: d => d.details.required ? this.$t("yes") : this.$t("no")
          },
          {
            caption: this.$t('allowInvalidInput'),
            datum: d => d.details.allowInvalid ? this.$t("yes") : this.$t("no")
          }
        ],
        actions: [
          {
            icon: 'trash',
            name: 'remove',
            tooltip: this.$t('removeParameter')
          },
          {
            icon: 'edit',
            name: 'edit',
            tooltip: this.$t('editParameter')
          },
          {
            name: 'moveUp',
            icon: 'arrow-up',
            tooltip: this.$t('operationDefinition.moveUp')
          },
          {
            name: 'moveDown',
            icon: 'arrow-down',
            tooltip: this.$t('operationDefinition.moveDown')
          }
        ]
      }
    }
  },
  watch: {
    operationDefinition: {
      handler(newVal) {
        this.internalDefinition = this.$_.cloneDeep(newVal);
        this.internalParameters = Object.keys(this.internalDefinition.model.dynaProperties).map(key => this.internalDefinition.model.dynaProperties[key])
        this.reloadIndices();
      },
      immediate: true
    },
    'focusedParam.details.type': {
      handler(newVal) {
        if (newVal == 'ENUM') {
          if (this.focusedParam.details.enumValues == undefined) {
            this.$set(this.focusedParam.details, 'enumValues', []);
          }
        }

      }
    }
  },
  computed: {
    ...mapGetters({
      'operationDefinitions': 'operationDefinitions/operationDefinitions',
      'currenciesOptions': 'currencies/currenciesOptions',
      'accounts': 'banking/accountsByInternal',
      'parties': 'parties/parties',
      'importDefinitionsOptions': "importDefinitions/processEventOptions"
    }),
    finalEntityFilter() {
      let filter = this.$_.cloneDeep(this.focusedParam.details.entityFilter) ?? {parameters: {}};
      if (this.focusedParam.details.entityType == 'operation') {
        filter.parameters.operationDefinition = {
          operator: "eq",
          type: "string",
          value: this.focusedParamOperationDefinition,
          negation: false
        }
      }
      return filter;
    },
    queryDefinition: {
      get() {
        return (fieldType) => {
          if (fieldType == 'operation') {
            var opDef = this.operationDefinitions.find(definition => definition.id == this.focusedParamOperationDefinition);

            return QueryUtils.OperationQueryDefinition(opDef,
                {
                  accounts: this.accounts(false),
                  parties: this.parties,
                  currencies: this.currenciesOptions
                });
          } else {
            return QueryUtils.getByType(fieldType.toLowerCase(), {
              accounts: this.accounts(false),
              parties: this.parties,
              currencies: this.currenciesOptions
            });
          }
        }
      }
    },
    operationDefinitionJson: {
      get() {
        return JSON.stringify(this.internalDefinition, null, 4);
      },
      set(newVal) {
        this.internalDefinition = JSON.parse(newVal);
      }
    }
  },
  methods: {
    save() {
      this.internalDefinition.model.dynaProperties = {}
      this.internalParameters.forEach(param => {
        this.internalDefinition.model.dynaProperties[param.name] = param;
      })
      this.$emit("save", this.internalDefinition);
    },
    addUniqueIndex() {
      this.operationIndices.push({
        operationDefinition: this.internalDefinition.id,
        fields: []
      });
    },
    saveParam() {
      let param = this.focusedParam;
      param.details.entityFilter = this.finalEntityFilter;
      let existing = this.internalParameters.find(p => p.name == this.originalParamName);
      if (existing == undefined) {
        this.internalParameters.push(param);
      } else {
        Object.assign(existing, param);
      }
      this.focusedParam = null;
      this.$modal.hide("paramModal");
    },
    reloadIndices() {
      this.$store.dispatch("operationIndices/FETCH_ALL").then(data => this.operationIndices = data.filter(datum => datum.operationDefinition == this.internalDefinition.id));
    },
    saveIndex(operationIndex) {
      this.deleteIndex(operationIndex)
          .then(() => {
            return this.$store.dispatch("operationIndices/SAVE", {...operationIndex, id: undefined})
          })
          .then(newIndex => {
            this.operationIndices.push(newIndex);
            this.$notify({
              group: 'application',
              title: `Successfully saved`,
              text: `Unique index has been saved`,
              type: "ok"
            })
          }, reason => {
            this.$notify({
              group: 'application',
              title: `Error during saving index`,
              text: `Unique index was not saved. \n\n ${reason.response.data?.message}`,
              type: "error",
              duration: 10000
            })
          })
    },
    deleteIndex(operationIndex) {
      const removeFromCollection = () => {
        const indexPos = this.operationIndices.indexOf(operationIndex);
        if (indexPos >= 0) {
          this.operationIndices.splice(indexPos, 1);
        }
        this.$notify({
          group: 'application',
          title: `Successfully deleted`,
          text: `Unique index has been deleted`,
          type: "ok"
        })
      }

      if (operationIndex.id) { //exists in DB?
        return this.$store.dispatch("operationIndices/DELETE", operationIndex)
            .then(removeFromCollection, reason => {
              this.$notify({
                group: 'application',
                title: `Error during deletion of index`,
                text: `Unique index was not deleted. \n\n ${reason.response.data?.message}`,
                type: "error",
                duration: 10000
              })
            })
      } else {
        removeFromCollection();
      }
      return Promise.resolve({});
    },
    toggleIndexField(operationIndex, fieldName) {
      const fldIndex = operationIndex.fields.findIndex(fld => fld == fieldName);
      if (fldIndex >= 0) {
        operationIndex.fields.splice(fldIndex, 1);
      } else {
        operationIndex.fields.push(fieldName);
      }
    },
    parameterEdit(focused) {
      this.focusedParam = this.$_.cloneDeep(focused);
      this.originalParamName = this.focusedParam.name;
      if (focused.details.type == 'ENTITY_REFERENCE'
          && focused.details.entityFilter
          && focused.details.entityType == 'operation') {

        let opDefinition = this.operationDefinitions.find(opDef => opDef.id == focused.details.entityFilter.parameters.operationDefinition.value);
        this.focusedParamOperationDefinition = opDefinition.id;
      }
      this.$modal.show("paramModal");
    },
    parameterMoveUp(entry) {
      let idx = this.internalParameters.indexOf(entry);
      if (idx > 0) {
        this.internalParameters.splice(idx, 1);
        this.internalParameters.splice(idx - 1, 0, entry);
      }
    },
    parameterMoveDown(entry) {
      let idx = this.internalParameters.indexOf(entry);
      if (idx < this.internalParameters.length - 1) {
        this.internalParameters.splice(idx, 1);
        this.internalParameters.splice(idx + 1, 0, entry);
      }
    },
    parameterAdd() {
      this.$modal.show("paramModal");
      this.originalParamName = "";
      this.focusedParam = {
        details: {}
      };
    },
    parameterRemove(entry) {
      let idx = this.internalParameters.indexOf(entry);
      this.internalParameters.splice(idx, 1);
    }
  }
}
</script>

<style lang="scss" scoped>

.columns-item {
  background-color: #39424B;
  color: var(--input-active-font-color);
  white-space: nowrap;
  border-radius: 5px;
  padding: 5px;
  margin: 5px;
  user-select: none;
  cursor: pointer;
}

</style>
